import { NormalizedPoint, NormalizedRect } from '../../models/chat.models';
import { PageRotation, UNIT_FRAME, boundsOfPoints, rotatePoint } from '../page-geometry/page-geometry';

export { normalizeRotation } from '../page-geometry/page-geometry';
export type { PageRotation } from '../page-geometry/page-geometry';

/**
 * Pure geometry for Preview page regions (Issue #1747).
 *
 * Two coordinate spaces meet here. *Display* space is what the user sees: 0–1 across the rendered
 * canvas, which PDF.js has already turned by the page's rotation. *Page* space is 0–1 across the page
 * before its rotation, with the origin at its top-left corner. Pointer and keyboard input arrive in
 * display space; everything stored or sent to the server is in page space.
 *
 * The turning arithmetic itself lives in `shared/page-geometry/page-geometry.ts`, the one library
 * every feature that places things on a page uses (Issue #2445). This file adds what is specific to
 * a region the user draws: its shape, its limits and how it is moved and resized.
 */

/** A corner of a selection's bounding box, named by compass direction on screen. */
export type ResizeHandle = 'nw' | 'ne' | 'se' | 'sw';

/** Fewest points a region polygon may have. */
export const MIN_POLYGON_POINTS = 3;

/** Most points sent to the server; matches the backend limit. */
export const MAX_POLYGON_POINTS = 512;

/** Lasso points closer together than this (fraction of the page) are dropped while drawing. */
export const LASSO_MIN_POINT_DISTANCE = 0.002;

/** Minimum selection size in PDF points, mirroring the backend check. */
export const MIN_REGION_LONG_EDGE_POINTS = 18;
export const MIN_REGION_SHORT_EDGE_POINTS = 4;

/** Smallest box a keyboard or handle resize may shrink to, as a fraction of the page. */
export const MIN_ADJUSTABLE_SIZE = 0.02;

/** Clamps a value to the 0–1 range. */
export function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

/**
 * Converts a displayed point to unrotated page space.
 *
 * PDF rotation is clockwise. Turning a page 90° clockwise carries its top-left corner to the top
 * right of the screen, so a displayed point (x, y) came from page point (y, 1 − x).
 */
export function displayToPage(point: NormalizedPoint, rotation: PageRotation): NormalizedPoint {
  return rotatePoint(point, UNIT_FRAME, -rotation);
}

/** Converts an unrotated page point to where it appears on the rotated display. */
export function pageToDisplay(point: NormalizedPoint, rotation: PageRotation): NormalizedPoint {
  return rotatePoint(point, UNIT_FRAME, rotation);
}

/** Converts every point of a displayed polygon to unrotated page space. */
export function polygonToPage(points: NormalizedPoint[], rotation: PageRotation): NormalizedPoint[] {
  return points.map(point => displayToPage(point, rotation));
}

/** Converts every point of a page-space polygon to where it appears on the rotated display. */
export function polygonToDisplay(points: NormalizedPoint[], rotation: PageRotation): NormalizedPoint[] {
  return points.map(point => pageToDisplay(point, rotation));
}

/** Bounding box of a set of points. An empty set yields an empty box at the origin. */
export function boundsOf(points: NormalizedPoint[]): NormalizedRect {
  return boundsOfPoints(points);
}

/** Absolute area of a polygon, in normalized units. */
export function polygonArea(points: NormalizedPoint[]): number {
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    sum += a.x * b.y - b.x * a.y;
  }
  return Math.abs(sum) / 2;
}

/** Ray-casting point-in-polygon test. */
export function pointInPolygon(point: NormalizedPoint, polygon: NormalizedPoint[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i];
    const b = polygon[j];
    const crosses = a.y > point.y !== b.y > point.y && point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x;
    if (crosses) inside = !inside;
  }
  return inside;
}

/** The four corners of a rectangle, clockwise from top-left. */
export function rectanglePolygon(rect: NormalizedRect): NormalizedPoint[] {
  return [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x + rect.width, y: rect.y + rect.height },
    { x: rect.x, y: rect.y + rect.height },
  ];
}

/** Rectangle spanned by two opposite corners, clamped to the page. */
export function rectangleFromCorners(a: NormalizedPoint, b: NormalizedPoint): NormalizedPoint[] {
  const x1 = clamp01(Math.min(a.x, b.x));
  const y1 = clamp01(Math.min(a.y, b.y));
  const x2 = clamp01(Math.max(a.x, b.x));
  const y2 = clamp01(Math.max(a.y, b.y));
  return rectanglePolygon({ x: x1, y: y1, width: x2 - x1, height: y2 - y1 });
}

/**
 * Reduces a freehand lasso to the points worth sending.
 *
 * Points closer than `minDistance` to the last kept point are dropped, and if the result still
 * exceeds the backend limit it is evenly subsampled. Both steps are deterministic, so the same
 * gesture always produces the same polygon.
 */
export function simplifyPolygon(
  points: NormalizedPoint[],
  minDistance = LASSO_MIN_POINT_DISTANCE,
  maxPoints = MAX_POLYGON_POINTS
): NormalizedPoint[] {
  const kept: NormalizedPoint[] = [];
  for (const raw of points) {
    const point = { x: clamp01(raw.x), y: clamp01(raw.y) };
    const last = kept[kept.length - 1];
    if (!last || Math.hypot(point.x - last.x, point.y - last.y) >= minDistance) {
      kept.push(point);
    }
  }
  if (kept.length <= maxPoints) return kept;
  const step = kept.length / maxPoints;
  const sampled: NormalizedPoint[] = [];
  for (let i = 0; i < maxPoints; i++) {
    sampled.push(kept[Math.floor(i * step)]);
  }
  return sampled;
}

/**
 * Moves a polygon by a displacement, stopping at the page edges.
 *
 * The whole shape stops rather than individual points clamping, so moving a selection against the
 * edge never distorts it.
 */
export function translatePolygon(points: NormalizedPoint[], dx: number, dy: number): NormalizedPoint[] {
  const box = boundsOf(points);
  const clampedDx = Math.max(-box.x, Math.min(1 - (box.x + box.width), dx));
  const clampedDy = Math.max(-box.y, Math.min(1 - (box.y + box.height), dy));
  return points.map(({ x, y }) => ({ x: x + clampedDx, y: y + clampedDy }));
}

/**
 * Resizes a polygon by dragging one corner of its bounding box, keeping the opposite corner fixed.
 *
 * Every point is scaled about the fixed corner, so a lasso keeps its shape while it grows or
 * shrinks. The box never inverts, never shrinks below `minSize`, and never leaves the page.
 */
export function resizePolygon(
  points: NormalizedPoint[],
  handle: ResizeHandle,
  dx: number,
  dy: number,
  minSize = MIN_ADJUSTABLE_SIZE
): NormalizedPoint[] {
  const box = boundsOf(points);
  if (box.width <= 0 || box.height <= 0) return points;
  const left = box.x;
  const top = box.y;
  const right = box.x + box.width;
  const bottom = box.y + box.height;

  const movesLeft = handle === 'nw' || handle === 'sw';
  const movesTop = handle === 'nw' || handle === 'ne';

  const anchorX = movesLeft ? right : left;
  const anchorY = movesTop ? bottom : top;

  let newWidth: number;
  let newHeight: number;
  if (movesLeft) {
    const newLeft = Math.max(0, Math.min(right - minSize, left + dx));
    newWidth = right - newLeft;
  } else {
    const newRight = Math.min(1, Math.max(left + minSize, right + dx));
    newWidth = newRight - left;
  }
  if (movesTop) {
    const newTop = Math.max(0, Math.min(bottom - minSize, top + dy));
    newHeight = bottom - newTop;
  } else {
    const newBottom = Math.min(1, Math.max(top + minSize, bottom + dy));
    newHeight = newBottom - top;
  }

  const scaleX = newWidth / box.width;
  const scaleY = newHeight / box.height;
  return points.map(({ x, y }) => ({
    x: clamp01(anchorX + (x - anchorX) * scaleX),
    y: clamp01(anchorY + (y - anchorY) * scaleY),
  }));
}

/**
 * Whether a page-space polygon is big enough for the server to accept.
 *
 * @param pageSize the page's unrotated size in PDF points; when unknown only the area is checked
 */
export function isLargeEnough(points: NormalizedPoint[], pageSize: { width: number; height: number } | null): boolean {
  if (points.length < MIN_POLYGON_POINTS || polygonArea(points) <= 0) return false;
  if (!pageSize) return true;
  const box = boundsOf(points);
  const width = box.width * pageSize.width;
  const height = box.height * pageSize.height;
  return (
    Math.max(width, height) >= MIN_REGION_LONG_EDGE_POINTS && Math.min(width, height) >= MIN_REGION_SHORT_EDGE_POINTS
  );
}

/** Rounds coordinates to six decimals, matching the backend's stored precision. */
export function roundPolygon(points: NormalizedPoint[]): NormalizedPoint[] {
  return points.map(({ x, y }) => ({ x: Math.round(x * 1e6) / 1e6, y: Math.round(y * 1e6) / 1e6 }));
}
